import React, { useState, useEffect, useRef } from 'react';
import { 
  QrCode, Camera, Search, User, Star, Crown, Plus, Check, X, Loader2, 
  Gift, History, DollarSign, Sparkles, ArrowRight, AlertCircle, Clock
} from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';
import { qrAPI, customerAPI, pointsAPI, servicesAPI, rewardsAPI } from '../../services/api';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import confetti from 'canvas-confetti';
import { format, formatDistanceToNow } from 'date-fns';

const StaffScanner = () => {
  // Scanner state
  const [scanning, setScanning] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  // Customer state
  const [customer, setCustomer] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [availableRewards, setAvailableRewards] = useState([]);
  const [loadingProfile, setLoadingProfile] = useState(false);

  // Services & forms
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState('');
  const [amount, setAmount] = useState('');
  const [adjustmentNotes, setAdjustmentNotes] = useState('');
  const [isManualAdjustment, setIsManualAdjustment] = useState(false);
  const [manualPoints, setManualPoints] = useState('');

  // Processing state
  const [processing, setProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState('add-points');

  // Confirmation dialogs
  const [showConfirmPoints, setShowConfirmPoints] = useState(false);
  const [showConfirmRedeem, setShowConfirmRedeem] = useState(false);
  const [selectedReward, setSelectedReward] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successData, setSuccessData] = useState(null);

  // Calculate points preview
  const [pointsPreview, setPointsPreview] = useState(null);

  useEffect(() => {
    fetchServices();
    return () => stopScanner();
  }, []);

  // Calculate points preview when amount changes
  useEffect(() => {
    if (amount && parseFloat(amount) > 0 && customer) {
      const basePoints = Math.floor(parseFloat(amount) * 10);
      const tierMultiplier = getTierMultiplier(customer.tier);
      const estimatedPoints = Math.floor(basePoints * tierMultiplier);
      setPointsPreview({
        base: basePoints,
        multiplier: tierMultiplier,
        total: estimatedPoints
      });
    } else {
      setPointsPreview(null);
    }
  }, [amount, customer]);

  const fetchServices = async () => {
    try {
      const response = await servicesAPI.getAll();
      setServices(response.data.services || []);
    } catch (error) {
      console.error('Failed to fetch services:', error);
    }
  };

  const fetchCustomerData = async (customerId) => {
    setLoadingProfile(true);
    try {
      const [transRes, rewardsRes] = await Promise.all([
        customerAPI.getTransactions(customerId, 10),
        rewardsAPI.getAll()
      ]);
      setTransactions(transRes.data.transactions || []);
      // Filter rewards customer can afford
      const allRewards = rewardsRes.data.rewards || [];
      setAvailableRewards(allRewards);
    } catch (error) {
      console.error('Failed to fetch customer data:', error);
    } finally {
      setLoadingProfile(false);
    }
  };

  const startScanner = async () => {
    try {
      if (!html5QrCodeRef.current) {
        html5QrCodeRef.current = new Html5Qrcode("qr-reader");
      }
      setScanning(true);
      await html5QrCodeRef.current.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 280, height: 280 } },
        onScanSuccess,
        () => {}
      );
    } catch (error) {
      console.error('Scanner error:', error);
      toast.error('Failed to start camera. Please check permissions.');
      setScanning(false);
    }
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current && html5QrCodeRef.current.isScanning) {
      try {
        await html5QrCodeRef.current.stop();
      } catch (error) {
        console.error('Stop scanner error:', error);
      }
    }
    setScanning(false);
  };

  const onScanSuccess = async (decodedText) => {
    try {
      const qrData = JSON.parse(decodedText);
      await stopScanner();
      
      if (qrData.type === 'customer') {
        const response = await qrAPI.verify(qrData);
        if (response.data.valid) {
          setCustomer(response.data.customer);
          fetchCustomerData(response.data.customer.id);
          toast.success(`Welcome ${response.data.customer.name || 'Customer'}!`);
        }
      } else if (qrData.type === 'reward_redemption') {
        toast.info('Reward QR detected - please use customer profile to redeem');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid or expired QR code');
    }
  };

  const handleManualSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const response = await customerAPI.getAll({ search: searchQuery });
      if (response.data.customers?.length > 0) {
        const foundCustomer = response.data.customers[0];
        setCustomer(foundCustomer);
        fetchCustomerData(foundCustomer.id);
        toast.success('Customer found!');
      } else {
        toast.error('No customer found');
      }
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const getTierMultiplier = (tier) => ({
    Bronze: 1.0,
    Silver: 1.25,
    Gold: 1.5,
    Platinum: 2.0
  })[tier] || 1.0;

  const getTierColor = (tier) => ({
    Bronze: 'tier-bronze',
    Silver: 'tier-silver',
    Gold: 'tier-gold',
    Platinum: 'tier-platinum',
  })[tier] || 'tier-bronze';

  const getTierNextThreshold = (tier, lifetime) => {
    const thresholds = { Bronze: 200, Silver: 500, Gold: 1000, Platinum: Infinity };
    const next = thresholds[tier];
    if (next === Infinity) return null;
    return { next: tier === 'Bronze' ? 'Silver' : tier === 'Silver' ? 'Gold' : 'Platinum', points: next - lifetime };
  };

  // Confirm and add points
  const handleConfirmAddPoints = () => {
    if (isManualAdjustment) {
      if (!manualPoints || parseInt(manualPoints) === 0) {
        toast.error('Please enter points amount');
        return;
      }
    } else {
      if (!amount || parseFloat(amount) <= 0) {
        toast.error('Please enter a valid bill amount');
        return;
      }
    }
    setShowConfirmPoints(true);
  };

  const executeAddPoints = async () => {
    setProcessing(true);
    setShowConfirmPoints(false);
    try {
      let response;
      if (isManualAdjustment) {
        // Manual adjustment - use a special endpoint or add directly
        response = await pointsAPI.add({
          customer_id: customer.id,
          amount: parseInt(manualPoints) / 10, // Convert points back to equivalent amount
          notes: adjustmentNotes || 'Manual adjustment'
        });
      } else {
        response = await pointsAPI.add({
          customer_id: customer.id,
          amount: parseFloat(amount),
          service_id: selectedService || null,
          notes: adjustmentNotes || null
        });
      }
      
      setSuccessData({
        type: 'points',
        ...response.data
      });
      setShowSuccess(true);
      
      // Update customer data
      setCustomer(prev => ({
        ...prev,
        current_points: response.data.new_balance,
        lifetime_points: (prev.lifetime_points || 0) + response.data.points_earned,
        tier: response.data.new_tier,
      }));
      
      // Refresh transactions
      fetchCustomerData(customer.id);
      
      // Confetti on tier upgrade
      if (response.data.tier_upgraded) {
        confetti({
          particleCount: 150,
          spread: 100,
          origin: { y: 0.6 },
          colors: ['#C9A689', '#4A154B', '#FFD700']
        });
      }
      
      // Reset form
      setAmount('');
      setManualPoints('');
      setSelectedService('');
      setAdjustmentNotes('');
      setIsManualAdjustment(false);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add points');
    } finally {
      setProcessing(false);
    }
  };

  // Redeem reward
  const handleRedeemClick = (reward) => {
    if (customer.current_points < reward.points_required) {
      toast.error(`Not enough points. Need ${reward.points_required - customer.current_points} more.`);
      return;
    }
    setSelectedReward(reward);
    setShowConfirmRedeem(true);
  };

  const executeRedeemReward = async () => {
    setProcessing(true);
    setShowConfirmRedeem(false);
    try {
      const response = await pointsAPI.redeem({
        customer_id: customer.id,
        reward_id: selectedReward.id
      });
      
      setSuccessData({
        type: 'redeem',
        reward: selectedReward,
        ...response.data
      });
      setShowSuccess(true);
      
      // Update customer data
      setCustomer(prev => ({
        ...prev,
        current_points: response.data.new_balance,
      }));
      
      // Refresh transactions
      fetchCustomerData(customer.id);
      
      setSelectedReward(null);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to redeem reward');
    } finally {
      setProcessing(false);
    }
  };

  const clearCustomer = () => {
    setCustomer(null);
    setTransactions([]);
    setAvailableRewards([]);
    setAmount('');
    setManualPoints('');
    setSelectedService('');
    setAdjustmentNotes('');
    setIsManualAdjustment(false);
    setActiveTab('add-points');
  };

  const formatTransactionDate = (dateStr) => {
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
    } catch {
      return dateStr;
    }
  };

  // Render scanner view when no customer
  if (!customer) {
    return (
      <div className="space-y-6 pt-12 lg:pt-0 animate-fadeIn">
        <div>
          <h1 className="font-serif text-3xl text-[#4A154B]">QR Scanner</h1>
          <p className="text-gray-600">Scan customer QR code to view profile and add points</p>
        </div>

        {/* Scanner */}
        <div className="glass rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-serif text-lg text-[#4A154B]">Scan Customer QR</h3>
            <Button
              onClick={scanning ? stopScanner : startScanner}
              variant={scanning ? "destructive" : "default"}
              className={scanning ? "" : "btn-primary"}
              data-testid="toggle-scanner-btn"
            >
              {scanning ? (
                <><X className="w-4 h-4 mr-2" /> Stop</>
              ) : (
                <><Camera className="w-4 h-4 mr-2" /> Start Scanner</>
              )}
            </Button>
          </div>
          
          <div 
            id="qr-reader" 
            ref={scannerRef}
            className={`w-full max-w-sm mx-auto rounded-xl overflow-hidden ${scanning ? '' : 'hidden'}`}
          />
          
          {!scanning && (
            <div className="w-full max-w-sm mx-auto h-72 bg-gradient-to-br from-[#F5E6D3] to-[#FFFFF0] rounded-xl flex flex-col items-center justify-center border-2 border-dashed border-[#C9A689]/50">
              <QrCode className="w-20 h-20 text-[#C9A689] mb-4 animate-pulse" />
              <p className="text-gray-600 font-medium">Ready to Scan</p>
              <p className="text-sm text-gray-400 mt-1">Point camera at customer's QR code</p>
            </div>
          )}
        </div>

        {/* Manual Search */}
        <div className="glass rounded-2xl p-6">
          <h3 className="font-serif text-lg text-[#4A154B] mb-4">Manual Lookup</h3>
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by email or phone..."
                className="pl-12 h-12"
                onKeyPress={(e) => e.key === 'Enter' && handleManualSearch()}
                data-testid="manual-search-input"
              />
            </div>
            <Button 
              onClick={handleManualSearch} 
              disabled={searching}
              className="btn-primary h-12 px-6"
              data-testid="search-btn"
            >
              {searching ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Search'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Customer Profile View
  const tierInfo = getTierNextThreshold(customer.tier, customer.lifetime_points);
  const affordableRewards = availableRewards.filter(r => r.can_redeem !== false && customer.current_points >= r.points_required);
  const lockedRewards = availableRewards.filter(r => customer.current_points < r.points_required);

  return (
    <div className="space-y-4 pt-12 lg:pt-0 animate-fadeIn">
      {/* Customer Header Card */}
      <div className="glass rounded-2xl overflow-hidden" data-testid="customer-profile-card">
        {/* Top Section - Gradient Background */}
        <div className="gradient-primary p-5 pb-16 relative">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={clearCustomer}
            className="absolute top-3 right-3 text-white/80 hover:text-white hover:bg-white/20"
            data-testid="clear-customer-btn"
          >
            <X className="w-4 h-4 mr-1" /> New Scan
          </Button>
          
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
              <User className="w-8 h-8 text-white" />
            </div>
            <div className="text-white">
              <h2 className="font-serif text-2xl">{customer.name || 'Guest'}</h2>
              <p className="text-white/80 text-sm">{customer.email}</p>
              {customer.phone && <p className="text-white/60 text-xs">{customer.phone}</p>}
            </div>
          </div>
        </div>
        
        {/* Points Card - Overlapping */}
        <div className="px-5 -mt-10 pb-5">
          <div className="bg-white rounded-xl shadow-lg p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Current Points</p>
                <p className="font-serif text-4xl text-[#4A154B]" data-testid="points-balance">{customer.current_points}</p>
              </div>
              <div className="text-right">
                <span className={`inline-flex items-center gap-1 px-4 py-2 rounded-full text-sm font-semibold ${getTierColor(customer.tier)}`}>
                  <Crown className="w-4 h-4" />
                  {customer.tier}
                </span>
                <p className="text-sm text-[#C9A689] mt-2 font-medium">
                  {getTierMultiplier(customer.tier)}x multiplier
                </p>
              </div>
            </div>
            
            {tierInfo && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-xs text-gray-500">
                  <Sparkles className="w-3 h-3 inline mr-1" />
                  {tierInfo.points} more points to {tierInfo.next}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Action Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 bg-[#F5E6D3] p-1 rounded-xl">
          <TabsTrigger 
            value="add-points" 
            className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-[#4A154B] data-[state=active]:shadow-sm"
            data-testid="tab-add-points"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Points
          </TabsTrigger>
          <TabsTrigger 
            value="redeem" 
            className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-[#4A154B] data-[state=active]:shadow-sm"
            data-testid="tab-redeem"
          >
            <Gift className="w-4 h-4 mr-2" /> Redeem
          </TabsTrigger>
          <TabsTrigger 
            value="history" 
            className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-[#4A154B] data-[state=active]:shadow-sm"
            data-testid="tab-history"
          >
            <History className="w-4 h-4 mr-2" /> History
          </TabsTrigger>
        </TabsList>

        {/* Add Points Tab */}
        <TabsContent value="add-points" className="mt-4">
          <div className="glass rounded-2xl p-5 space-y-5">
            {/* Toggle Manual Adjustment */}
            <div className="flex items-center justify-between pb-4 border-b border-[#F5E6D3]">
              <span className="text-sm font-medium text-gray-700">
                {isManualAdjustment ? 'Manual Point Adjustment' : 'Bill-Based Points'}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsManualAdjustment(!isManualAdjustment)}
                className="text-[#C9A689] hover:text-[#4A154B]"
                data-testid="toggle-manual-adjustment"
              >
                {isManualAdjustment ? 'Switch to Bill' : 'Manual Adjust'}
              </Button>
            </div>

            {!isManualAdjustment ? (
              <>
                {/* Service Selection */}
                <div>
                  <label className="form-label text-sm">Service (Optional)</label>
                  <Select value={selectedService} onValueChange={setSelectedService}>
                    <SelectTrigger className="h-12" data-testid="service-select">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map((service) => (
                        <SelectItem key={service.id} value={service.id}>
                          {service.name} - ${service.price}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Bill Amount */}
                <div>
                  <label className="form-label text-sm">Bill Amount</label>
                  <div className="relative">
                    <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      className="pl-12 h-14 text-xl font-semibold"
                      data-testid="bill-amount-input"
                    />
                  </div>
                </div>

                {/* Points Preview */}
                {pointsPreview && (
                  <div className="bg-gradient-to-r from-[#F5E6D3] to-[#FFFFF0] rounded-xl p-4" data-testid="points-preview">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">Points to Earn</p>
                        <p className="font-serif text-3xl text-[#4A154B]">+{pointsPreview.total}</p>
                      </div>
                      <div className="text-right text-sm text-gray-500">
                        <p>Base: {pointsPreview.base} pts</p>
                        <p className="text-[#C9A689] font-medium">{customer.tier} {pointsPreview.multiplier}x</p>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Manual Points Entry */}
                <div>
                  <label className="form-label text-sm">Points Amount</label>
                  <div className="relative">
                    <Star className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#C9A689]" />
                    <Input
                      type="number"
                      value={manualPoints}
                      onChange={(e) => setManualPoints(e.target.value)}
                      placeholder="Enter points (+ or -)"
                      className="pl-12 h-14 text-xl font-semibold"
                      data-testid="manual-points-input"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Use negative values to subtract points (e.g., -50 for corrections)
                  </p>
                </div>
              </>
            )}

            {/* Notes */}
            <div>
              <label className="form-label text-sm">Notes (Optional)</label>
              <Textarea
                value={adjustmentNotes}
                onChange={(e) => setAdjustmentNotes(e.target.value)}
                placeholder={isManualAdjustment ? "Reason for adjustment..." : "Additional notes..."}
                rows={2}
                className="resize-none"
                data-testid="notes-input"
              />
            </div>

            {/* Submit Button */}
            <Button
              onClick={handleConfirmAddPoints}
              disabled={processing || (!isManualAdjustment && !amount) || (isManualAdjustment && !manualPoints)}
              className="w-full h-14 btn-primary text-lg"
              data-testid="add-points-btn"
            >
              {processing ? (
                <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Processing...</>
              ) : (
                <><Plus className="w-5 h-5 mr-2" /> {isManualAdjustment ? 'Apply Adjustment' : 'Add Points'}</>
              )}
            </Button>
          </div>
        </TabsContent>

        {/* Redeem Tab */}
        <TabsContent value="redeem" className="mt-4">
          <div className="space-y-4">
            {/* Affordable Rewards */}
            {affordableRewards.length > 0 && (
              <div>
                <h4 className="font-medium text-[#4A154B] mb-3 flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#8FBC8F]" />
                  Available to Redeem ({affordableRewards.length})
                </h4>
                <div className="space-y-3">
                  {affordableRewards.map((reward) => (
                    <div
                      key={reward.id}
                      className="glass rounded-xl p-4 flex items-center justify-between card-hover"
                      data-testid={`reward-${reward.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-[#8FBC8F]/20 flex items-center justify-center">
                          <Gift className="w-6 h-6 text-[#8FBC8F]" />
                        </div>
                        <div>
                          <h5 className="font-medium text-[#4A154B]">{reward.name}</h5>
                          <p className="text-sm text-[#C9A689] font-semibold">{reward.points_required} pts</p>
                        </div>
                      </div>
                      <Button
                        onClick={() => handleRedeemClick(reward)}
                        className="btn-primary"
                        data-testid={`redeem-btn-${reward.id}`}
                      >
                        Redeem
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Locked Rewards */}
            {lockedRewards.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-500 mb-3 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Need More Points ({lockedRewards.length})
                </h4>
                <div className="space-y-2 opacity-60">
                  {lockedRewards.slice(0, 3).map((reward) => (
                    <div
                      key={reward.id}
                      className="bg-gray-50 rounded-xl p-4 flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-gray-200 flex items-center justify-center">
                          <Gift className="w-5 h-5 text-gray-400" />
                        </div>
                        <div>
                          <h5 className="font-medium text-gray-600">{reward.name}</h5>
                          <p className="text-sm text-gray-500">{reward.points_required} pts</p>
                        </div>
                      </div>
                      <span className="text-sm text-gray-400">
                        {reward.points_required - customer.current_points} more
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {availableRewards.length === 0 && (
              <div className="text-center py-12 glass rounded-2xl">
                <Gift className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No rewards available</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="mt-4">
          <div className="glass rounded-2xl p-5">
            <h4 className="font-medium text-[#4A154B] mb-4 flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#C9A689]" />
              Last 10 Transactions
            </h4>
            
            {loadingProfile ? (
              <div className="space-y-3">
                {[1,2,3].map(i => <div key={i} className="skeleton h-16 rounded-lg" />)}
              </div>
            ) : transactions.length > 0 ? (
              <div className="space-y-3">
                {transactions.map((t) => (
                  <div
                    key={t.id}
                    className="flex items-center justify-between py-3 px-4 bg-[#F5E6D3]/30 rounded-xl"
                    data-testid={`transaction-${t.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        t.type === 'earned' ? 'bg-[#8FBC8F]/20' : 'bg-[#C9A689]/20'
                      }`}>
                        {t.type === 'earned' ? (
                          <Plus className="w-5 h-5 text-[#8FBC8F]" />
                        ) : (
                          <Gift className="w-5 h-5 text-[#C9A689]" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-[#4A154B] text-sm">
                          {t.service_name || t.reward_name || (t.type === 'earned' ? 'Points Earned' : 'Reward Redeemed')}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatTransactionDate(t.created_at)}
                          {t.amount && <span className="ml-2">(${t.amount.toFixed(2)})</span>}
                        </p>
                      </div>
                    </div>
                    <span className={`font-semibold text-lg ${
                      t.type === 'earned' ? 'text-[#8FBC8F]' : 'text-[#C74545]'
                    }`}>
                      {t.type === 'earned' ? '+' : ''}{t.points}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <History className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">No transactions yet</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Confirmation Dialog - Add Points */}
      <Dialog open={showConfirmPoints} onOpenChange={setShowConfirmPoints}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#4A154B]">Confirm Points</DialogTitle>
            <DialogDescription>
              Review the transaction before confirming
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="bg-[#F5E6D3] rounded-xl p-4 text-center">
              <p className="text-sm text-gray-600 mb-1">
                {isManualAdjustment ? 'Points Adjustment' : 'Points to Award'}
              </p>
              <p className="font-serif text-4xl text-[#4A154B]">
                {isManualAdjustment ? (
                  parseInt(manualPoints) >= 0 ? `+${manualPoints}` : manualPoints
                ) : (
                  pointsPreview ? `+${pointsPreview.total}` : '0'
                )}
              </p>
            </div>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Customer</span>
                <span className="font-medium text-[#4A154B]">{customer?.name || customer?.email}</span>
              </div>
              {!isManualAdjustment && amount && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Bill Amount</span>
                  <span className="font-medium">${parseFloat(amount).toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-500">New Balance</span>
                <span className="font-medium text-[#C9A689]">
                  {customer?.current_points + (isManualAdjustment ? parseInt(manualPoints || 0) : (pointsPreview?.total || 0))} pts
                </span>
              </div>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowConfirmPoints(false)}>
              Cancel
            </Button>
            <Button onClick={executeAddPoints} className="btn-primary" data-testid="confirm-add-points">
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog - Redeem */}
      <Dialog open={showConfirmRedeem} onOpenChange={setShowConfirmRedeem}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#4A154B]">Confirm Redemption</DialogTitle>
            <DialogDescription>
              This action cannot be undone
            </DialogDescription>
          </DialogHeader>
          
          {selectedReward && (
            <div className="py-4 space-y-4">
              <div className="bg-[#F5E6D3] rounded-xl p-4 text-center">
                <Gift className="w-10 h-10 text-[#C9A689] mx-auto mb-2" />
                <p className="font-serif text-xl text-[#4A154B]">{selectedReward.name}</p>
                <p className="text-sm text-gray-600">{selectedReward.description}</p>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Points Cost</span>
                  <span className="font-medium text-[#C74545]">-{selectedReward.points_required}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Current Balance</span>
                  <span className="font-medium">{customer?.current_points} pts</span>
                </div>
                <div className="flex justify-between pt-2 border-t">
                  <span className="text-gray-500">New Balance</span>
                  <span className="font-medium text-[#C9A689]">
                    {customer?.current_points - selectedReward.points_required} pts
                  </span>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowConfirmRedeem(false)}>
              Cancel
            </Button>
            <Button onClick={executeRedeemReward} className="btn-primary" data-testid="confirm-redeem">
              Confirm Redemption
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Success Dialog */}
      <Dialog open={showSuccess} onOpenChange={setShowSuccess}>
        <DialogContent className="max-w-sm">
          <div className="text-center py-4">
            <div className="w-20 h-20 rounded-full bg-[#8FBC8F]/20 flex items-center justify-center mx-auto mb-4">
              <Check className="w-10 h-10 text-[#8FBC8F]" />
            </div>
            
            {successData?.type === 'points' ? (
              <>
                <h3 className="font-serif text-2xl text-[#4A154B] mb-2">Points Added!</h3>
                <div className="bg-[#F5E6D3] rounded-xl p-4 mb-4">
                  <p className="font-serif text-4xl text-[#4A154B]">+{successData.points_earned}</p>
                </div>
                
                {successData.tier_upgraded && (
                  <div className="bg-gradient-to-r from-[#C9A689] to-[#4A154B] text-white rounded-xl p-4 mb-4">
                    <Crown className="w-6 h-6 mx-auto mb-1" />
                    <p className="font-semibold">Tier Upgraded to {successData.new_tier}!</p>
                  </div>
                )}
                
                <div className="flex justify-center gap-6 text-sm">
                  <div>
                    <p className="text-gray-500">New Balance</p>
                    <p className="font-semibold text-[#4A154B]">{successData.new_balance} pts</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Tier</p>
                    <p className="font-semibold text-[#C9A689]">{successData.new_tier}</p>
                  </div>
                </div>
              </>
            ) : (
              <>
                <h3 className="font-serif text-2xl text-[#4A154B] mb-2">Reward Redeemed!</h3>
                <div className="bg-[#F5E6D3] rounded-xl p-4 mb-4">
                  <Gift className="w-8 h-8 text-[#C9A689] mx-auto mb-2" />
                  <p className="font-serif text-lg text-[#4A154B]">{successData?.reward?.name}</p>
                </div>
                <div className="text-sm">
                  <p className="text-gray-500">New Balance</p>
                  <p className="font-semibold text-[#4A154B]">{successData?.new_balance} pts</p>
                </div>
              </>
            )}
            
            <p className="text-xs text-gray-400 mt-4">
              Confirmation email sent to customer
            </p>
          </div>
          
          <DialogFooter>
            <Button onClick={() => setShowSuccess(false)} className="w-full btn-primary">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffScanner;
